<template>
  <div
    v-on-click-outside="() => (showMenu = false)"
    class="mx-auto mb-5 flex flex-col items-center"
  >
    <div class="h-full w-full">
      <div class="relative flex h-full flex-col items-center">
        <span
          v-show="!!value?.length"
          class="absolute top-0 left-0 z-10 -mt-4 ml-2-5 bg-white px-1 text-xs leading-6 text-gray-400"
        >
          {{ placeholder }}
        </span>
        <div class="h-full w-full">
          <div
            data-cy="multiselect-dropdown-toggle"
            class="z-10 flex h-full cursor-pointer rounded-sm border border-gray-200 bg-white px-1 py-1"
            @click="toggleMenu"
          >
            <div
              v-if="value.length"
              class="flex h-full w-full flex-auto flex-wrap"
              :class="!isAbsolutePositioned && 'mr-2 overflow-hidden'"
            >
              <lf-badge
                removable
                :truncate="!isAbsolutePositioned"
                :is-rounded="isRounded"
                @click:remove="removeFirstOption"
              >
                <span
                  ref="textContainerRef"
                  v-tooltip="{
                    content: formatOption(firstOption),
                    onShow: () => isSelectedTextTruncated,
                  }"
                  :class="
                    !isAbsolutePositioned &&
                    'block w-full overflow-hidden text-ellipsis whitespace-nowrap'
                  "
                >
                  {{ formatOption(firstOption) }}
                </span>
              </lf-badge>
              <lf-badge
                v-if="value.length > 1"
                v-tooltip="otherSelectedOptions"
                removable
                @click:remove="removeOtherOptions"
              >
                + {{ otherSelectedOptionsCount }}
              </lf-badge>
            </div>
            <span v-else class="flex cursor-pointer items-center pl-3">
              {{ placeholder }}
            </span>
            <div
              class="flex items-center py-1 pr-1 pl-2 text-gray-300"
              :class="!isAbsolutePositioned && 'relative w-full flex-1'"
            >
              <button
                type="button"
                class="h-6 cursor-pointer text-gray-600 outline-hidden focus:outline-hidden"
              >
                <span
                  class="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-2"
                >
                  <icon-base
                    :class="{ 'rotate-180 transform': showMenu }"
                    height="6"
                    width="10"
                    :icon="IconDropdownArrow"
                    :icon-color="UTIL_COLORS.DEFAULT"
                  />
                </span>
              </button>
            </div>
          </div>
        </div>
        <div
          v-if="showMenu && !disabled"
          class="right-0 z-10 w-full rounded-md bg-white py-2-5 text-sm text-gray-500 shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
          :class="isAbsolutePositioned ? 'absolute top-full' : 'mt-1'"
        >
          <div v-if="options.length > 3" class="border-b px-2 pb-2">
            <search-input v-model="searchTerm" no-margin no-padding />
          </div>
          <div
            v-if="noResultsFound"
            data-cy="multiselect-dropdown-no-results"
            class="px-4 pt-2"
          >
            {{ $t("COMMON.NO_RESULTS") }}
          </div>
          <ul v-else class="dropdown-options max-h-50 overflow-y-auto">
            <li
              v-for="(option, key) in options"
              v-show="filterByName(option.name)"
              :key="key"
              :data-cy="option.id"
              class="flex cursor-pointer justify-start space-x-4 overflow-auto py-2 pl-4 hover:bg-gray-200"
            >
              <input
                :id="`attr-option-${key}`"
                type="checkbox"
                class="h-4 w-4 cursor-pointer place-self-center rounded-xs border-gray-300"
                :checked="value.some((attr) => attr.id === option.id)"
                :value="option.id"
                @click="handleCheckboxClick"
              />
              <label class="cursor-pointer" :for="`attr-option-${key}`">
                {{ formatOption(option.name) }}
              </label>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import startCase from "lodash/startCase";
import upperFirst from "lodash/upperFirst";

import { UTIL_COLORS } from "@/helpers/constants";
import IconDropdownArrow from "@/components/icons/IconDropdownArrow.vue";
import { useElementStatus } from "@/hooks/elements";

import SearchInput from "./SearchInput.vue";

import type { MultiSelectOption } from "@/models/options";
import type { IAttribute, IStepAttribute } from "@/models/orchestration";
import type { PropType } from "vue";

const emit = defineEmits(["change"]);

const props = defineProps({
  value: {
    type: Array as PropType<IStepAttribute[] | Pick<MultiSelectOption, "id">[]>,
    default: () => [],
  },
  options: {
    type: Array as PropType<IAttribute[] | MultiSelectOption[]>,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: "",
  },
  isAbsolutePositioned: {
    type: Boolean,
    default: true,
  },
  scrollableParent: {
    type: String,
    required: false,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  allowTrim: {
    type: Boolean,
    default: true,
  },
  canFormatOption: {
    type: Boolean,
    default: true,
  },
  isRounded: {
    type: Boolean,
    default: false,
  },
});

const showMenu = ref(false);
const searchTerm = ref("");

const textContainerRef = ref<HTMLElement | null>(null);
const { isTextTruncated } = useElementStatus();

const isSelectedTextTruncated = isTextTruncated(textContainerRef);

const firstOption = computed(() => {
  const attrId = props.value[0].id;
  return props.options.find((attr) => attr.id === attrId)?.name ?? "";
});

const otherSelectedOptionsCount = computed(() => props.value.length - 1);

const otherSelectedOptions = computed(() =>
  props.value
    .slice(1)
    .map((attr) => {
      const attribute = props.options.find(
        (attribute) => attribute.id === attr.id
      );
      return attribute?.name || "";
    })
    .filter(Boolean)
    .join(", ")
);

const formatOption = (option: string): string => {
  if (!props.canFormatOption) {
    return option;
  }

  return props.allowTrim
    ? startCase(option)
    : option
        .split(" ")
        .map((word) => upperFirst(word))
        .join(" ");
};

const toggleMenu = () => {
  if (props.disabled) {
    return;
  }

  showMenu.value = !showMenu.value;
};

const filterByName = (option: string) =>
  !searchTerm.value ||
  option.toLowerCase().includes(searchTerm.value.toLowerCase());

const noResultsFound = computed(() => {
  return (
    !props.options.length ||
    !props.options.some((option) => filterByName(option.name))
  );
});

const removeFirstOption = () => {
  if (props.disabled) {
    return;
  }

  const [, ...rest] = props.value;
  emit("change", rest);
};

const removeOtherOptions = () => {
  if (props.disabled) {
    return;
  }

  const [first] = props.value;
  emit("change", [first]);
};

const optionIsIStepAttribute = (
  option: IAttribute | MultiSelectOption
): option is IAttribute => "context" in option;

const handleCheckboxClick = (event: MouseEvent) => {
  const { value, checked } = event.target as HTMLInputElement;

  if (!checked) {
    emit(
      "change",
      props.value.filter((attr) => String(attr.id) !== String(value))
    );
    return;
  }

  const option = props.options.find(
    (option) => String(option.id) === String(value)
  );

  if (!option) {
    return props.value;
  }

  const newValue: IStepAttribute = {
    id: option.id,
  };

  if (
    optionIsIStepAttribute(option) &&
    option?.options?.refresh_ttl_hours != null
  ) {
    newValue.options = {
      refresh_ttl_hours: Number(option.options.refresh_ttl_hours),
    };
  }

  emit("change", [...props.value, newValue]);
};
</script>
